import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import instance from '../../api/Axios';

const LocalAmenities = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch local amenities for this provider
      const response = await instance.get('/api/amenities/?amenity_type=local');
      setAmenities(response.data);
    } catch (error) {
      console.error('Error fetching amenities:', error);
      setError('Failed to load amenities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Amenity name is required';
    }

    // Check for duplicate names (excluding current item when editing)
    const existingNames = amenities
      .filter(amenity => editingAmenity ? amenity.id !== editingAmenity.id : true)
      .map(amenity => amenity.name.toLowerCase());

    if (existingNames.includes(formData.name.toLowerCase())) {
      newErrors.name = 'An amenity with this name already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        amenity_type: 'local',
        is_active: formData.is_active
      };

      if (editingAmenity) {
        // Update existing amenity
        await instance.put(`/api/amenities/${editingAmenity.id}/`, payload);
        setSuccess('Amenity updated successfully!');
      } else {
        // Create new amenity
        await instance.post('/api/amenities/', payload);
        setSuccess('Amenity created successfully!');
      }

      // Reset form and close modal
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
      setEditingAmenity(null);
      setModal(false);
      
      // Refresh the list
      fetchAmenities();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error saving amenity:', error);
      if (error.response?.data) {
        // Handle specific field errors from backend
        const backendErrors = error.response.data;
        if (typeof backendErrors === 'object') {
          setErrors(backendErrors);
        } else {
          setError(backendErrors.detail || 'Failed to save amenity. Please try again.');
        }
      } else {
        setError('Failed to save amenity. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setFormData({
      name: amenity.name,
      description: amenity.description || '',
      is_active: amenity.is_active
    });
    setErrors({});
    setError('');
    setModal(true);
  };

  const handleDelete = async (amenity) => {
    if (!window.confirm(`Are you sure you want to delete "${amenity.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await instance.delete(`/api/amenities/${amenity.id}/`);
      setSuccess('Amenity deleted successfully!');
      fetchAmenities();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting amenity:', error);
      setError('Failed to delete amenity. It may be in use by existing room types.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleStatus = async (amenity) => {
    try {
      const payload = {
        ...amenity,
        is_active: !amenity.is_active
      };

      await instance.put(`/api/amenities/${amenity.id}/`, payload);
      setSuccess(`Amenity ${payload.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchAmenities();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating amenity status:', error);
      setError('Failed to update amenity status. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const openCreateModal = () => {
    setEditingAmenity(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
    setErrors({});
    setError('');
    setModal(true);
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading amenities...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">My Custom Amenities</h2>
          <small className="text-muted">Manage your property-specific amenities that will display alongside global amenities</small>
        </div>
        <Button color="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Add Custom Amenity
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert color="success" className="mb-4">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {error && (
        <Alert color="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Amenities Table */}
      <Card>
        <CardHeader>
          <CardTitle tag="h5" className="mb-0">
            <i className="bi bi-star me-2"></i>
            My Custom Amenities ({amenities.length})
          </CardTitle>
        </CardHeader>
        <CardBody>
          {amenities.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-star" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">No Custom Amenities</h5>
              <p>Create custom amenities specific to your properties. These will be available alongside global amenities when creating room types.</p>
              <Button color="primary" onClick={openCreateModal}>
                <i className="bi bi-plus-circle me-2"></i>
                Create First Custom Amenity
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {amenities.map((amenity) => (
                    <tr key={amenity.id}>
                      <td>
                        <strong>{amenity.name}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {amenity.description || '-'}
                        </small>
                      </td>
                      <td>
                        <Badge 
                          color={amenity.is_active ? 'success' : 'warning'}
                          pill
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleStatus(amenity)}
                          title="Click to toggle status"
                        >
                          {amenity.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {amenity.created_at ? new Date(amenity.created_at).toLocaleDateString() : '-'}
                        </small>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle size="sm" color="outline-primary">
                            <i className="bi bi-three-dots-vertical"></i>
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => handleEdit(amenity)}>
                              <i className="bi bi-pencil me-2"></i>
                              Edit
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem 
                              onClick={() => handleDelete(amenity)}
                              className="text-danger"
                            >
                              <i className="bi bi-trash me-2"></i>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModal(!modal)}>
            {editingAmenity ? 'Edit Custom Amenity' : 'Create New Custom Amenity'}
          </ModalHeader>
          <ModalBody>
            {error && (
              <Alert color="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            <FormGroup>
              <Label for="name">
                Amenity Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                invalid={!!errors.name}
                placeholder="e.g., Game Room, Rooftop Terrace, Study Pod"
                maxLength={100}
              />
              {errors.name && <div className="text-danger small">{errors.name}</div>}
              <small className="form-text text-muted">
                A descriptive name for this custom amenity specific to your property
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Optional description of what this amenity includes..."
                rows={3}
                maxLength={500}
              />
              <small className="form-text text-muted">
                Optional detailed description ({formData.description.length}/500)
              </small>
            </FormGroup>

            <FormGroup check>
              <Input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
              />
              <Label check for="is_active">
                Active (available for use in your room types)
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {editingAmenity ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {editingAmenity ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default LocalAmenities;
